import { useState } from 'react';
import { Badge, Button, EmptyState } from '@assist/ui';
import { endpoints } from '../state/api.js';
import { useSession } from '../state/session.js';
import { useResource } from '../lib/useResource.js';
import { Screen } from '../components/Screen.js';

/**
 * HASIL PENCARIAN GLOBAL — Figma `117:783` · lintas modul · rute `/search?q=`.
 *
 * EMPAT KONDISI PETA §5.1, DAN YANG KEEMPAT ADALAH YANG PALING PENTING
 * -------------------------------------------------------------------
 * baku · tanpa hasil · kueri terlalu pendek (< 3 karakter) · **hanya sebagian jenis yang
 * boleh dilihat**.
 *
 * Yang terakhir bukan kondisi tampilan, ia pernyataan kepada pengguna. Peta §7 menulis:
 * "Pencarian global, laporan, dan tolok ukur konsorsium semuanya tunduk pada aturan ini —
 * dan layar-layarnya MENYATAKANNYA KEPADA PENGGUNA SECARA TERTULIS." Jadi layar ini
 * menuliskan satu kalimat di kakinya: yang tidak muncul di sini bukan berarti tidak ada,
 * ia berarti bukan hak Anda melihatnya. Tanpa kalimat itu, seorang auditor yang tidak
 * menemukan sebuah dokumen akan menyimpulkan dokumen itu tidak pernah dibuat.
 *
 * `QUERY_TOO_SHORT` ditangani SEBELUM permintaan dikirim, bukan sebagai galat dari server.
 * Alasannya diukur: kotak cari mengirim per ketukan, dan `websearch_to_tsquery` atas satu
 * huruf memindai seluruh indeks. Servernya tetap menolaknya — dua lapis, dan yang di klien
 * hanya untuk tidak mengetuk basis data tiga kali sebelum kata pertama selesai.
 */
export function SearchResults({
  query,
  onOpen,
}: {
  query: string;
  onOpen: (route: string) => void;
}) {
  const { t } = useSession();
  const [type, setType] = useState<string>('');
  const cukupPanjang = query.trim().length >= 3;

  const results = useResource(
    async () => endpoints.search(query.trim(), type === '' ? undefined : type),
    [query, type],
    { enabled: cukupPanjang },
  );

  if (!cukupPanjang) {
    return (
      <Screen title={t('search.title')} resource={null}>
        {() => (
          <EmptyState title={t('search.tooShort.title')} body={t('search.tooShort.body')} />
        )}
      </Screen>
    );
  }

  return (
    <Screen
      title={t('search.title')}
      subtitle={t('search.subtitle', { query: query.trim() })}
      resource={results}
      aside={null}
    >
      {(data) => (
        <>
          {/*
            Chip penyaring per jenis. Jumlahnya datang dari SERVER dan dihitung dari baris
            yang SAMA dengan yang dikembalikan — bukan dari kueri kedua tanpa penyaring.
            Sebuah chip yang berkata "12 temuan" di atas daftar berisi 3 adalah angka yang
            tidak dapat dipertanggungjawabkan.
          */}
          <div className="filters">
            <button
              type="button"
              className={type === '' ? 'is-active' : ''}
              aria-pressed={type === ''}
              onClick={() => setType('')}
            >
              {t('search.filter.all')}
            </button>
            {data.facets.map((f) => (
              <button
                key={f.entity_type}
                type="button"
                className={type === f.entity_type ? 'is-active' : ''}
                aria-pressed={type === f.entity_type}
                onClick={() => setType(f.entity_type)}
              >
                {t(`search.type.${f.entity_type}`)} ({f.count})
              </button>
            ))}
          </div>

          {data.results.length === 0 ? (
            <EmptyState title={t('search.none.title')} body={t('search.none.body')} />
          ) : (
            <ul className="results">
              {data.results.map((r) => (
                <li key={`${r.entity_type}:${r.entity_id}`} className="card">
                  <p className="results__meta">
                    <Badge tone="neutral">{t(`search.type.${r.entity_type}`)}</Badge>
                    {r.ref_code ? <span className="card__muted"> {r.ref_code}</span> : null}
                  </p>
                  <h2 className="card__title">
                    <button className="linkish" type="button" onClick={() => onOpen(r.route)}>
                      {r.title}
                    </button>
                  </h2>
                  <p className="card__muted">{r.snippet}</p>
                </li>
              ))}
            </ul>
          )}

          {/*
            PERNYATAAN ISOLASI, tertulis (peta §7). Ia bukan catatan kaki hukum: ia
            keterangan yang mencegah kesimpulan yang salah dari daftar yang pendek.
          */}
          <p className="card__muted">{t('search.isolationNote')}</p>

          {data.page_info.has_more ? (
            <p className="page__actions">
              <Button variant="secondary" onClick={() => void results.reload()}>
                {t('search.moreResults')}
              </Button>
            </p>
          ) : null}
        </>
      )}
    </Screen>
  );
}
